import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { SiteHeader } from '../../components/SiteLayout';
import { Button } from '../../components/ui/button';
import { ArrowLeft, ArrowsClockwise, Warning, CheckCircle, ClockCounterClockwise } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SLATE = '#0B1426';
const MUTED = '#5B6577';
const HAIRLINE = '#ECECEC';
const CORAL = '#E63946';
const IVORY = '#FBF8F1';

const STATUS_META = {
  green:  { label: 'All systems nominal',   color: '#03543F', bg: '#DEF7EC' },
  yellow: { label: 'Attention required',    color: '#723B13', bg: '#FEF3C7' },
  red:    { label: 'Action needed',         color: '#9B1C1C', bg: '#FDE8E8' },
};

export default function OpsControlPage() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [resolving, setResolving] = useState(null);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/ops/snapshot`, { withCredentials: true });
      setSnapshot(r.data);
    } catch (e) {
      console.error('ops snapshot failed', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 60s while the tab is visible. Pause polling when
    // the tab is hidden to avoid silent network/credit burn on long-idle
    // admin sessions. Resume immediately when the tab becomes visible
    // again so the operator sees current state on focus.
    let t = null;
    const start = () => {
      if (t) return;
      t = setInterval(fetchData, 60000);
    };
    const stop = () => {
      if (!t) return;
      clearInterval(t);
      t = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchData]);

  const resolve = async (id) => {
    setResolving(id);
    try {
      await axios.post(`${API_URL}/api/admin/ops/watchdog/${id}/resolve`, {}, { withCredentials: true });
      await fetchData();
    } catch (e) {
      console.error('resolve failed', e);
    } finally {
      setResolving(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFFFFF' }}>
        <p className="text-[14px]" style={{ color: MUTED }}>Loading Mission Control…</p>
      </div>
    );
  }

  const meta = STATUS_META[snapshot?.status] || STATUS_META.green;
  const ai = snapshot?.ai_quality || {};
  const wf = snapshot?.workflows || {};
  const val = snapshot?.assessment_validations || {};

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }} data-testid="ops-control-page">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <Link to="/admin"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] mb-6 transition-colors"
          style={{ color: MUTED }}
          data-testid="ops-back-to-admin"
        >
          <ArrowLeft size={12} weight="bold" /> Control room
        </Link>

        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] mb-3" style={{ color: CORAL }}>Mission Control</p>
            <h1
              className="font-light leading-tight tracking-tight mb-3"
              style={{ color: SLATE, fontFamily: '"Cormorant Garamond", "Times New Roman", serif', fontSize: 'clamp(2rem, 4vw, 2.75rem)' }}
            >
              Autonomous operations.
            </h1>
            <div className="flex items-center gap-3">
              <span
                className="inline-flex items-center px-3 py-1 rounded-sm text-[11px] font-bold uppercase tracking-[0.22em]"
                style={{ color: meta.color, backgroundColor: meta.bg }}
                data-testid="ops-status-pill"
              >
                {meta.label}
              </span>
              <span className="text-[11px]" style={{ color: MUTED }}>
                Updated {new Date(snapshot.generated_at).toLocaleTimeString()} · auto-refresh 30s
              </span>
            </div>
          </div>
          <Button
            onClick={fetchData} disabled={refreshing}
            data-testid="ops-refresh"
            className="h-10 px-4 rounded-full text-[11px] font-bold uppercase tracking-[0.22em] flex-shrink-0"
            style={{ backgroundColor: SLATE, color: '#FFFFFF' }}
          >
            <ArrowsClockwise size={13} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing' : 'Refresh'}
          </Button>
        </div>

        {/* Stats — 3 panels, side by side on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px mb-8" style={{ backgroundColor: HAIRLINE }}>
          <StatPanel
            title="AI quality" subtitle="last 24h"
            primary={ai.total_24h || 0} primaryLabel="invocations"
            secondary={ai.flag_rate_pct ?? 0} secondaryLabel={`${ai.flag_rate_pct ?? 0}% flagged`}
            tone={ai.flag_rate_pct > 15 ? 'warn' : 'ok'}
            testId="ops-stat-ai"
          />
          <StatPanel
            title="Workflows" subtitle="last 24h"
            primary={wf.total_24h || 0} primaryLabel="started"
            secondary={wf.failed_24h || 0} secondaryLabel={`${wf.failed_24h || 0} failed`}
            tone={wf.failed_24h > 5 ? 'warn' : 'ok'}
            testId="ops-stat-workflows"
          />
          <StatPanel
            title="Assessment validation" subtitle="last 24h"
            primary={val.total_24h || 0} primaryLabel="generated"
            secondary={val.failed_24h || 0} secondaryLabel={`${val.failed_24h || 0} failed`}
            tone={val.failed_24h > 0 ? 'warn' : 'ok'}
            testId="ops-stat-assessments"
          />
        </div>

        {/* Stuck workflows */}
        <Section title="Stuck workflows" subtitle={`${wf.stuck_count || 0} running past SLA`} testId="ops-stuck-section">
          {(wf.stuck || []).length === 0 ? (
            <EmptyState icon={CheckCircle} text="No stuck workflows. Watchdog all clear." />
          ) : (
            <ul style={{ borderTop: `1px solid ${HAIRLINE}` }}>
              {wf.stuck.map((w) => (
                <li
                  key={w.id}
                  className="flex items-start gap-4 px-5 py-4"
                  style={{ borderBottom: `1px solid ${HAIRLINE}` }}
                  data-testid={`stuck-row-${w.id}`}
                >
                  <Warning size={18} weight="duotone" style={{ color: CORAL, marginTop: 2, flexShrink: 0 }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="text-[14px] font-semibold" style={{ color: SLATE }}>{w.kind}</p>
                      <span className="text-[12px]" style={{ color: MUTED }}>·</span>
                      <p className="text-[12px] font-mono" style={{ color: MUTED }}>{w.key?.slice(0, 24)}</p>
                    </div>
                    <p className="text-[12px] mt-1" style={{ color: MUTED }}>
                      Started {new Date(w.started_at).toLocaleString()} · overdue {w.overdue_seconds}s (SLA {w.sla_seconds}s)
                    </p>
                  </div>
                  <Button
                    onClick={() => resolve(w.id)}
                    disabled={resolving === w.id}
                    data-testid={`resolve-${w.id}`}
                    className="h-8 px-3 rounded-sm text-[10px] font-bold uppercase tracking-wider flex-shrink-0"
                    style={{ backgroundColor: '#FFFFFF', color: SLATE, border: `1px solid ${HAIRLINE}` }}
                  >
                    {resolving === w.id ? 'Resolving' : 'Cancel'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Recent AI quality flags */}
        <Section title="Recent AI quality flags" subtitle={`${(ai.recent_flags || []).length} flagged outputs · 7d`} testId="ops-ai-section">
          {(ai.recent_flags || []).length === 0 ? (
            <EmptyState icon={CheckCircle} text="No AI quality flags in the last 7 days." />
          ) : (
            <ul style={{ borderTop: `1px solid ${HAIRLINE}` }}>
              {ai.recent_flags.map((f) => (
                <li
                  key={f.id}
                  className="flex items-start gap-4 px-5 py-4"
                  style={{ borderBottom: `1px solid ${HAIRLINE}` }}
                  data-testid={`ai-flag-row-${f.id}`}
                >
                  <ClockCounterClockwise size={16} weight="duotone" style={{ color: MUTED, marginTop: 2 }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="text-[14px] font-semibold" style={{ color: SLATE }}>{f.context}</p>
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                        style={{ color: f.recommendation === 'retry' ? '#9B1C1C' : '#723B13',
                                 backgroundColor: f.recommendation === 'retry' ? '#FDE8E8' : '#FEF3C7' }}
                      >
                        {f.recommendation}
                      </span>
                      <span className="text-[11px]" style={{ color: MUTED }}>score {f.score}</span>
                    </div>
                    <p className="text-[12px] mt-1" style={{ color: MUTED }}>
                      Flags: {f.flags?.join(', ') || '—'} · {new Date(f.at).toLocaleString()}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Recently failed workflows */}
        <Section title="Recently failed workflows" subtitle={`${(wf.recent_failed || []).length} · 7d`} testId="ops-failed-section">
          {(wf.recent_failed || []).length === 0 ? (
            <EmptyState icon={CheckCircle} text="No failed workflows in the last 7 days." />
          ) : (
            <ul style={{ borderTop: `1px solid ${HAIRLINE}` }}>
              {wf.recent_failed.map((w) => (
                <li
                  key={w.id}
                  className="flex items-start gap-4 px-5 py-4"
                  style={{ borderBottom: `1px solid ${HAIRLINE}` }}
                  data-testid={`failed-row-${w.id}`}
                >
                  <Warning size={16} weight="duotone" style={{ color: MUTED, marginTop: 2 }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-semibold" style={{ color: SLATE }}>{w.kind}</p>
                    <p className="text-[12px] mt-1" style={{ color: MUTED }}>
                      {w.error || 'No error message'} · {new Date(w.ended_at || w.started_at).toLocaleString()}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </main>
    </div>
  );
}

function StatPanel({ title, subtitle, primary, primaryLabel, secondary, secondaryLabel, tone, testId }) {
  return (
    <div className="px-5 py-5" style={{ backgroundColor: '#FFFFFF' }} data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: MUTED }}>{title}</p>
      <p className="text-[10px] mt-0.5" style={{ color: MUTED }}>{subtitle}</p>
      <p
        className="text-4xl tabular-nums leading-none mt-3"
        style={{
          color: tone === 'warn' && primary > 0 ? CORAL : SLATE,
          fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
          fontWeight: 300,
        }}
      >
        {primary}
      </p>
      <p className="text-[11px] mt-1" style={{ color: MUTED }}>{primaryLabel}</p>
      <p className="text-[12px] mt-3 pt-3" style={{ color: tone === 'warn' ? CORAL : MUTED, borderTop: `1px solid ${HAIRLINE}` }}>
        {secondaryLabel}
      </p>
    </div>
  );
}

function Section({ title, subtitle, children, testId }) {
  return (
    <section className="mb-8" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[18px] font-semibold tracking-tight" style={{ color: SLATE }}>{title}</h2>
        <p className="text-[11px]" style={{ color: MUTED }}>{subtitle}</p>
      </div>
      <div className="rounded-sm overflow-hidden" style={{ border: `1px solid ${HAIRLINE}`, backgroundColor: '#FFFFFF' }}>
        {children}
      </div>
    </section>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="px-5 py-10 text-center">
      {Icon && <Icon size={28} weight="duotone" style={{ color: '#9CA3AF', display: 'inline-block', marginBottom: 8 }} />}
      <p className="text-[13px]" style={{ color: MUTED }}>{text}</p>
    </div>
  );
}
